import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import {
  AlertTriangle,
  Clock,
  IndianRupee,
  Copy,
  XCircle,
  RefreshCw,
  Edit2,
  Save,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Exceptions = () => {
  const [exceptions, setExceptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingTask, setEditingTask] = useState(null);
  const [editValue, setEditValue] = useState({ rate: "", duration_words: "" });

  const fetchExceptions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/exceptions`);
      setExceptions(response.data);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to load exceptions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExceptions();
  }, []);

  const handleEditTask = (task, field) => {
    setEditingTask({ id: task.id, field });
    setEditValue({
      rate: task.rate || "",
      duration_words: task.duration_words || "",
    });
  };

  const handleSaveTask = async (taskId) => {
    try {
      const updateData = {};
      if (editValue.rate) updateData.rate = parseFloat(editValue.rate);
      if (editValue.duration_words) updateData.duration_words = parseFloat(editValue.duration_words);

      await axios.put(`${API}/tasks/${taskId}`, updateData);
      toast.success("Task updated");
      setEditingTask(null);
      fetchExceptions();
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const getExceptionCount = (key) => {
    if (!exceptions) return 0;
    return exceptions[key]?.length || 0;
  };

  const totalExceptions =
    getExceptionCount("missing_duration") +
    getExceptionCount("missing_rate") +
    getExceptionCount("duplicates") +
    getExceptionCount("not_found") +
    getExceptionCount("unverified");

  if (loading) {
    return (
      <div className="page-container">
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  const ExceptionTable = ({ data, type }) => {
    if (!data || data.length === 0) {
      return (
        <div className="text-center py-8 text-slate-500">
          No exceptions of this type found.
        </div>
      );
    }

    return (
      <ScrollArea className="h-[400px]">
        {/* Grid Header */}
        <div 
          className="grid bg-slate-50 border-b border-slate-200 sticky top-0 z-10"
          style={{ 
            gridTemplateColumns: '1fr 1fr 1fr 1.5fr 1fr 1fr 1fr 1fr',
            padding: '12px 20px'
          }}
        >
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider text-center">Atlas Job ID</div>
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider text-center">PO Number</div>
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider text-center">Task</div>
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider text-center">Resource</div>
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider text-center">Status</div>
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider text-center">Duration/Words</div>
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider text-center">Rate</div>
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider text-center">Verification</div>
        </div>
        {/* Grid Body */}
        {data.map((task, idx) => (
          <div 
            key={task.id || idx}
            className="grid border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
            style={{ 
              gridTemplateColumns: '1fr 1fr 1fr 1.5fr 1fr 1fr 1fr 1fr',
              padding: '12px 20px',
              alignItems: 'center'
            }}
          >
            <div className="text-sm font-mono text-center">{task.atlas_job_id || "-"}</div>
            <div className="text-sm font-mono text-center">{task.po_number || "-"}</div>
            <div className="text-sm text-center">
              <Badge className="badge badge-info text-[10px]">{task.task || "-"}</Badge>
            </div>
            <div className="text-sm text-center leading-snug" style={{ wordBreak: 'break-word' }}>{task.resource || "-"}</div>
            <div className="text-sm text-center">
              <Badge className="badge badge-neutral text-[10px]">{task.task_status || "-"}</Badge>
            </div>
            <div className="text-sm text-center">
              {editingTask?.id === task.id && editingTask?.field === "duration" ? (
                <div className="flex items-center gap-1 justify-center">
                  <Input
                    type="number"
                    value={editValue.duration_words}
                    onChange={(e) => setEditValue({ ...editValue, duration_words: e.target.value })}
                    className="w-20 h-7 text-xs"
                    data-testid={`input-duration-${task.id}`}
                  />
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleSaveTask(task.id)} data-testid={`save-duration-${task.id}`}>
                    <Save className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-1 justify-center">
                  <span className={task.duration_words == null ? "text-red-600 font-medium" : "font-mono"}>
                    {task.duration_words?.toFixed(2) || "Missing"}
                  </span>
                  {type === "missing_duration" && (
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleEditTask(task, "duration")} data-testid={`edit-duration-${task.id}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              )}
            </div>
            <div className="text-sm text-center">
              {editingTask?.id === task.id && editingTask?.field === "rate" ? (
                <div className="flex items-center gap-1 justify-center">
                  <Input
                    type="number"
                    value={editValue.rate}
                    onChange={(e) => setEditValue({ ...editValue, rate: e.target.value })}
                    className="w-20 h-7 text-xs"
                    data-testid={`input-rate-${task.id}`}
                  />
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleSaveTask(task.id)} data-testid={`save-rate-${task.id}`}>
                    <Save className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-1 justify-center">
                  <span className={task.rate == null ? "text-amber-600 font-medium" : "font-mono"}>
                    {task.rate?.toFixed(2) || "Missing"}
                  </span>
                  {type === "missing_rate" && (
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleEditTask(task, "rate")} data-testid={`edit-rate-${task.id}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-center">
              <Badge
                className={
                  task.verification_confirmed === "YES"
                    ? "badge badge-success text-[10px]"
                    : task.verification_confirmed === "DUPLICATE"
                    ? "badge badge-warning text-[10px]"
                    : task.verification_confirmed === "NOT FOUND"
                    ? "badge badge-error text-[10px]"
                    : "badge badge-neutral text-[10px]"
                }
              >
                {task.verification_confirmed || "Pending"}
              </Badge>
            </div>
          </div>
        ))}
      </ScrollArea>
    );
  };

  return (
    <div className="page-container" data-testid="exceptions-page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">Exceptions</h1>
          <p className="page-subtitle">Review and fix data issues before invoice generation</p>
        </div>
        <div className="flex items-center gap-4">
          <Badge variant="outline" className="px-4 py-2">
            <AlertTriangle className="w-4 h-4 mr-2 text-amber-500" />
            {totalExceptions} Total Exceptions
          </Badge>
          <Button onClick={fetchExceptions} variant="outline" className="btn-secondary" data-testid="refresh-exceptions-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <div className="p-4 rounded-lg bg-red-50 border border-red-100">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-red-500" />
            <span className="text-xs text-red-600 font-medium">Missing Duration</span>
          </div>
          <p className="text-2xl font-bold text-red-700">{getExceptionCount("missing_duration")}</p>
        </div>
        <div className="p-4 rounded-lg bg-amber-50 border border-amber-100">
          <div className="flex items-center gap-2 mb-2">
            <IndianRupee className="w-4 h-4 text-amber-500" />
            <span className="text-xs text-amber-600 font-medium">Missing Rate</span>
          </div>
          <p className="text-2xl font-bold text-amber-700">{getExceptionCount("missing_rate")}</p>
        </div>
        <div className="p-4 rounded-lg bg-orange-50 border border-orange-100">
          <div className="flex items-center gap-2 mb-2">
            <Copy className="w-4 h-4 text-orange-500" />
            <span className="text-xs text-orange-600 font-medium">Duplicates</span>
          </div>
          <p className="text-2xl font-bold text-orange-700">{getExceptionCount("duplicates")}</p>
        </div>
        <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="w-4 h-4 text-slate-500" />
            <span className="text-xs text-slate-600 font-medium">Not Found</span>
          </div>
          <p className="text-2xl font-bold text-slate-700">{getExceptionCount("not_found")}</p>
        </div>
        <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-blue-500" />
            <span className="text-xs text-blue-600 font-medium">Unverified</span>
          </div>
          <p className="text-2xl font-bold text-blue-700">{getExceptionCount("unverified")}</p>
        </div>
      </div>

      {/* Exception Tabs */}
      <Card className="card">
        <Tabs defaultValue="missing_duration">
          <TabsList className="m-4 bg-slate-100 p-1 rounded-lg">
            <TabsTrigger
              value="missing_duration"
              className="data-[state=active]:bg-white px-4 py-2 rounded-md text-sm"
              data-testid="tab-missing-duration"
            >
              Missing Duration ({getExceptionCount("missing_duration")})
            </TabsTrigger>
            <TabsTrigger
              value="missing_rate"
              className="data-[state=active]:bg-white px-4 py-2 rounded-md text-sm"
              data-testid="tab-missing-rate"
            >
              Missing Rate ({getExceptionCount("missing_rate")})
            </TabsTrigger>
            <TabsTrigger
              value="duplicates"
              className="data-[state=active]:bg-white px-4 py-2 rounded-md text-sm"
              data-testid="tab-duplicates"
            >
              Duplicates ({getExceptionCount("duplicates")})
            </TabsTrigger>
            <TabsTrigger
              value="not_found"
              className="data-[state=active]:bg-white px-4 py-2 rounded-md text-sm"
              data-testid="tab-not-found"
            >
              Not Found ({getExceptionCount("not_found")})
            </TabsTrigger>
            <TabsTrigger
              value="unverified"
              className="data-[state=active]:bg-white px-4 py-2 rounded-md text-sm"
              data-testid="tab-unverified"
            >
              Unverified ({getExceptionCount("unverified")})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="missing_duration" className="p-0">
            <ExceptionTable data={exceptions?.missing_duration} type="missing_duration" />
          </TabsContent>
          <TabsContent value="missing_rate" className="p-0">
            <ExceptionTable data={exceptions?.missing_rate} type="missing_rate" />
          </TabsContent>
          <TabsContent value="duplicates" className="p-0">
            <ExceptionTable data={exceptions?.duplicates} type="duplicates" />
          </TabsContent>
          <TabsContent value="not_found" className="p-0">
            <ExceptionTable data={exceptions?.not_found} type="not_found" />
          </TabsContent>
          <TabsContent value="unverified" className="p-0">
            <ExceptionTable data={exceptions?.unverified} type="unverified" />
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
};

export default Exceptions;
